/**
 * Hook para manejar comisiones de transacciones
 * Basado en la lógica de Front/app/send/page.tsx
 */
"use client";

import { useState, useEffect } from 'react';


export interface CommissionFees {
  fee_one: number;
  fee_two: number; 
  fee_three: number;
}

export interface CommissionResult {
  fees: CommissionFees | null;
  currentFee: number;
  totalAmount: number;
  isLoading: boolean;
  error: string | null;
}

export function useCommissionFees(amount: number): CommissionResult {
  const [fees, setFees] = useState<CommissionFees | null>(null);
  const [currentFee, setCurrentFee] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Cargar fees iniciales del backend
  useEffect(() => {
    const loadFees = async () => {
      try {
        setIsLoading(true);
        const response = await fetch('/api/getCommissionFees');
        const data = await response.json();

        if (response.ok) {
          console.log('[CommissionFees] Loaded fees from backend:', data);
          setFees(data);
          setError(null);
        } else {
          console.error('[CommissionFees] Error response:', data);
          setError(data.message || 'Error loading commission fees');
        }
      } catch (err) {
        console.error('Error loading commission fees:', err);
        setError('Network error loading commission fees');
      } finally {
        setIsLoading(false);
      }
    };

    loadFees();
  }, []);

  // Calcular fee basado en el monto (misma lógica que Front)
  useEffect(() => {
    if (!fees || !amount || amount <= 0) {
      setCurrentFee(0);
      return;
    }

    let fee = 0;

    // Aplicar comisiones por rangos (misma lógica que Front)
    if (amount <= 30) {
      fee = fees.fee_one;
      console.log(`[CommissionFees] Amount ${amount} <= 30, using fee_one: ${fee}`);
    } else if (amount <= 250) {
      fee = fees.fee_two;
      console.log(`[CommissionFees] Amount ${amount} <= 250, using fee_two: ${fee}`);
    } else if (amount <= 1000) {
      fee = fees.fee_three;
      console.log(`[CommissionFees] Amount ${amount} <= 1000, using fee_three: ${fee}`);
    } else {
      fee = fees.fee_three; // Para montos > 1000 usar fee_three
      console.log(`[CommissionFees] Amount ${amount} > 1000, using fee_three: ${fee}`);
    }

    console.log(`[CommissionFees] Final fee calculation - Amount: ${amount}, Fee: ${fee}, Fees object:`, fees);
    setCurrentFee(fee);
  }, [fees, amount]);

  return {
    fees,
    currentFee,
    totalAmount: amount + currentFee,
    isLoading,
    error,
  };
}